import { createSimpleStore } from '@/store/factories/simpleStoreFactory';
import { Dtos_V2_CrewDto } from '@/common/infrastructure/gql/generated/schema';
import type { CrewToAdd } from '@/jobs/application/types/JobTypes';

export type JobCrewTemporaryState = {
  temporaryCrewByJob: Record<number, Dtos_V2_CrewDto[]>;
  temporaryCrewMembersToAddByJob: Record<number, CrewToAdd[]>;
};

export type JobCrewTemporaryActions = {
  setTemporaryCrew: (jobId: number, crew: Dtos_V2_CrewDto[]) => void;
  addTemporaryCrew: (jobId: number, crewMember: Dtos_V2_CrewDto) => void;
  removeTemporaryCrew: (jobId: number, crewId: number) => void;
  setTemporaryCrewMembersToAdd: (jobId: number, crewMembersToAdd: CrewToAdd[]) => void;
  addTemporaryCrewMemberToAdd: (jobId: number, crewMemberToAdd: CrewToAdd) => void;
  removeTemporaryCrewMemberToAdd: (jobId: number, crewId: number) => void;
  getTemporaryCrew: (jobId: number) => Dtos_V2_CrewDto[];
  getTemporaryCrewMembersToAdd: (jobId: number) => CrewToAdd[];
  clearJob: (jobId: number) => void;
  clearAll: () => void;
};

export const useJobCrewTemporaryStore = createSimpleStore<JobCrewTemporaryState, JobCrewTemporaryActions>({
  name: 'job-crew-temporary',
  initialState: {
    temporaryCrewByJob: {},
    temporaryCrewMembersToAddByJob: {}
  },
  actions: (set, get) => ({
    setTemporaryCrew: (jobId: number, crew: Dtos_V2_CrewDto[]) => {
      const next = { ...get().temporaryCrewByJob, [jobId]: crew };
      set({ temporaryCrewByJob: next } as JobCrewTemporaryState);
    },
    addTemporaryCrew: (jobId: number, crewMember: Dtos_V2_CrewDto) => {
      const current = get().temporaryCrewByJob[jobId] || [];
      if (current.some((c) => c.id === crewMember.id)) {
        // Update existing crew member
        const updated = current.map((c) => (c.id === crewMember.id ? crewMember : c));
        const next = { ...get().temporaryCrewByJob, [jobId]: updated };
        set({ temporaryCrewByJob: next } as JobCrewTemporaryState);
      } else {
        // Add new crew member
        const next = { ...get().temporaryCrewByJob, [jobId]: [...current, crewMember] };
        set({ temporaryCrewByJob: next } as JobCrewTemporaryState);
      }
    },
    removeTemporaryCrew: (jobId: number, crewId: number) => {
      const current = get().temporaryCrewByJob[jobId] || [];
      const filtered = current.filter((c) => c.id !== crewId);
      const next = { ...get().temporaryCrewByJob, [jobId]: filtered };
      set({ temporaryCrewByJob: next } as JobCrewTemporaryState);
    },
    setTemporaryCrewMembersToAdd: (jobId: number, crewMembersToAdd: CrewToAdd[]) => {
      const next = { ...get().temporaryCrewMembersToAddByJob, [jobId]: crewMembersToAdd };
      set({ temporaryCrewMembersToAddByJob: next } as JobCrewTemporaryState);
    },
    addTemporaryCrewMemberToAdd: (jobId: number, crewMemberToAdd: CrewToAdd) => {
      const current = get().temporaryCrewMembersToAddByJob[jobId] || [];
      const filtered = current.filter((cm) => cm.id !== crewMemberToAdd.id);
      const next = { ...get().temporaryCrewMembersToAddByJob, [jobId]: [...filtered, crewMemberToAdd] };
      set({ temporaryCrewMembersToAddByJob: next } as JobCrewTemporaryState);
    },
    removeTemporaryCrewMemberToAdd: (jobId: number, crewId: number) => {
      const current = get().temporaryCrewMembersToAddByJob[jobId] || [];
      const filtered = current.filter((cm) => cm.id !== crewId);
      const next = { ...get().temporaryCrewMembersToAddByJob, [jobId]: filtered };
      set({ temporaryCrewMembersToAddByJob: next } as JobCrewTemporaryState);
    },
    getTemporaryCrew: (jobId: number) => {
      return get().temporaryCrewByJob[jobId] || [];
    },
    getTemporaryCrewMembersToAdd: (jobId: number) => {
      return get().temporaryCrewMembersToAddByJob[jobId] || [];
    },
    clearJob: (jobId: number) => {
      const crew = { ...get().temporaryCrewByJob };
      const crewMembersToAdd = { ...get().temporaryCrewMembersToAddByJob };
      delete crew[jobId];
      delete crewMembersToAdd[jobId];
      set({
        temporaryCrewByJob: crew,
        temporaryCrewMembersToAddByJob: crewMembersToAdd
      } as JobCrewTemporaryState);
    },
    clearAll: () => {
      set({
        temporaryCrewByJob: {},
        temporaryCrewMembersToAddByJob: {}
      } as JobCrewTemporaryState);
    }
  })
});
